import React, { useMemo } from 'react';
import {
  getDocumentById,
  listDocuments,
  type Document,
  type Widget,
} from '@airhead-games/shared/content';
import DocumentListingCard from '@/content/rendering/documentListings/DocumentListingCard';
import DocumentListingGrid from '@/content/rendering/documentListings/DocumentListingGrid';

interface WidgetRelatedDocumentsProps {
  widget: Extract<Widget, { type: 'relatedDocuments' }>;
  currentDocument?: Document;
}

const WidgetRelatedDocuments: React.FC<WidgetRelatedDocumentsProps> = ({
  widget,
  currentDocument,
}) => {
  const documents = useMemo(() => {
    const explicitDocuments = widget.documentIds
      ?.map((id) => getDocumentById(id))
      .filter((document): document is Document => Boolean(document));

    const resolved = explicitDocuments?.length
      ? explicitDocuments
      : currentDocument
        ? listDocuments({
            relatedTo: currentDocument.id,
            kind: currentDocument.kind === 'blog' ? 'blog' : undefined,
            sortBy:
              currentDocument.kind === 'blog' ? 'publishedAt-desc' : undefined,
          })
        : [];

    const maxItems = currentDocument?.kind === 'blog' ? 4 : widget.limit;
    return maxItems ? resolved.slice(0, maxItems) : resolved;
  }, [currentDocument, widget]);

  if (documents.length === 0) {
    return null;
  }

  const isBlog = currentDocument?.kind === 'blog';

  return (
    <section
      className={
        isBlog
          ? 'flex flex-col gap-4 border-t border-border pt-8'
          : 'flex flex-col gap-4'
      }
    >
      <div className="flex flex-col gap-4">
        <h2 className="font-heading text-2xl leading-tight font-semibold">
          {isBlog ? 'Related posts' : 'Related'}
        </h2>
      </div>
      {isBlog ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {documents.map((document, index) => (
            <div
              key={document.id}
              className={
                index >= 3
                  ? 'hidden xl:block'
                  : index >= 2
                    ? 'hidden lg:block'
                    : undefined
              }
            >
              <DocumentListingCard document={document} />
            </div>
          ))}
        </div>
      ) : (
        <DocumentListingGrid documents={documents} />
      )}
    </section>
  );
};

export default WidgetRelatedDocuments;
